import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddRemoveIcon from '@sharedCR/common/AddRemoveIcon';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { COLOR_PALETTE } from 'constants/ui';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { jsonStringify } from 'utils/json';

import { selectIsSellerPaidFieldsVisible } from 'store/loanApplications/selectors';
import { selectFeeTypes } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../LoanFees.testIds';
import {
  addButtonStyles,
  getTableStyles,
} from '../../AccordionDetailsView/FeeTable/FeeTable.styles';
import useTriggerAddIfRequested from '../hooks/useTriggerAddIfRequested';

import FeeTableRow from './FeeTableRow';

const FeeTable = ({
  isRecordingType = false,
  prefix = 'set_1',
  data,
  isFeeProviderColumnVisible = true,
  isNonBorrowerPaidFieldsIncluded,
  isWithHeader,
  leSection,
  isLoading,
  customHeader,
  meta: {
    handleChange,
    validateField,
    validateForm,
    setInForm,
    setForm,
    setIsTouched,
    validationErrors: vldErrors,
    setEditedSection,
    pendingCredits,
    addPendingCredit,
    removePendingCredit,
  },
}) => {
  const feeTypes = useSelector(selectFeeTypes);
  const isSellerPaidFieldsVisible = useSelector(selectIsSellerPaidFieldsVisible);

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const isFinancedAmountColumnVisible = useMemo(
    () =>
      !isRecordingType &&
      data.some(
        ({ feeTypeId, includeIntoTotalLoanAmount, pendingChangesActionType }) =>
          pendingChangesActionType !== PendingChangesActionType.DELETED &&
          (!!feeTypes[feeTypeId?.value]?.includeIntoTotalLoanAmount ||
            !!includeIntoTotalLoanAmount),
      ),
    [isRecordingType, data, feeTypes],
  );

  const stringifiedConfigFormFees = jsonStringify(
    data.reduce((acc, formField) => {
      const feeTypeId = formField.feeTypeId?.value;

      if (feeTypeId) {
        acc[feeTypeId] = formField.pendingChangesActionType !== PendingChangesActionType.DELETED;
      }

      return acc;
    }, {}),
  );

  const onAddFee = useCallback(() => {
    setIsTouched(true);

    setForm(prev => ({
      ...prev,
      [prefix]: [
        ...prev[prefix],
        {
          isLocallyAdded: true,
          index: prev[prefix].length,
          pendingChangesActionType: PendingChangesActionType.ADDED,
          recordingType: '',
          isWithRecordingType: isRecordingType,
          leSection,
        },
      ],
    }));
  }, [setForm, setIsTouched, prefix, isRecordingType, leSection]);

  useTriggerAddIfRequested(setEditedSection, onAddFee);

  const onDeleteFee = useCallback(
    e => {
      const index = Number(e.currentTarget.dataset.index);
      setIsTouched(true);

      setForm(prev => ({
        ...prev,
        [prefix]: prev[prefix].reduce((acc, fee) => {
          if (fee.index === index && fee.isLocallyAdded) {
            return acc;
          }

          acc.push(
            fee.index === index
              ? {
                  ...fee,
                  isDeleted: true,
                  pendingChangesActionType: PendingChangesActionType.DELETED,
                }
              : fee,
          );

          return acc;
        }, []),
      }));
    },
    [setForm, setIsTouched, prefix],
  );

  return (
    <>
      <TruncatedProvider />

      <TableContainer
        sx={getTableStyles({
          isFeeProviderColumnVisible,
          isFinancedAmountColumnVisible,
          isOpened: true,
          isNonBorrowerPaidFieldsIncluded,
        })}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <Table>
          {isWithHeader &&
            (customHeader || (
              <TableHead>
                <TableRow>
                  <TableCell>Fee</TableCell>
                  {isNonBorrowerPaidFieldsIncluded && isFeeProviderColumnVisible && (
                    <TableCell>Fee Provider</TableCell>
                  )}

                  <TableCell align="center" colSpan={2}>
                    <div>Borrower-Paid</div>
                    <div>
                      <span className="text-right">At Closing</span>
                      <span className="text-right">Before Closing</span>
                    </div>
                  </TableCell>

                  {isNonBorrowerPaidFieldsIncluded && (
                    <>
                      {isSellerPaidFieldsVisible && (
                        <TableCell align="center" colSpan={2}>
                          <div>Seller-Paid</div>
                          <div>
                            <span className="text-right">At Closing</span>
                            <span className="text-right">Before Closing</span>
                          </div>
                        </TableCell>
                      )}

                      <TableCell align="right">Paid By Others</TableCell>
                    </>
                  )}

                  {isFinancedAmountColumnVisible && (
                    <TableCell align="right">Financed Amount</TableCell>
                  )}

                  <TableCell />
                </TableRow>
              </TableHead>
            ))}

          <TableBody>
            {data
              .filter(
                ({ pendingChangesActionType }) =>
                  pendingChangesActionType !== PendingChangesActionType.DELETED,
              )
              .map(({ index, ...fee }) => {
                return (
                  <FeeTableRow
                    {...fee}
                    addPendingCredit={addPendingCredit}
                    handleChange={handleChange}
                    index={index}
                    isFeeProviderColumnVisible={isFeeProviderColumnVisible}
                    isFinancedAmountColumnVisible={isFinancedAmountColumnVisible}
                    isLoading={isLoading}
                    isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
                    key={index}
                    leSection={leSection}
                    pendingCredits={pendingCredits}
                    prefix={prefix}
                    removePendingCredit={removePendingCredit}
                    setForm={setForm}
                    setInForm={setInForm}
                    setIsTouched={setIsTouched}
                    stringifiedConfigFormFees={stringifiedConfigFormFees}
                    validateField={validateField}
                    validateForm={validateForm}
                    vldErrors={vldErrors}
                    onDeleteFee={onDeleteFee}
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex justify-start my-12">
        <Button
          data-testid={LoanFeesTestIds.EDIT_FEE_TABLE_ADD_BUTTON}
          disabled={isLoading}
          sx={addButtonStyles}
          variant="text"
          onClick={onAddFee}
        >
          <AddRemoveIcon color={COLOR_PALETTE.BRAND[6]} />
          <Typography variant="regular14">Add Fee</Typography>
        </Button>
      </div>
    </>
  );
};

FeeTable.propTypes = {
  ...WithLoadingPropTypes,
  data: PropTypes.array.isRequired,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool.isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  leSection: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
    setInForm: PropTypes.func.isRequired,
    setForm: PropTypes.func.isRequired,
    setIsTouched: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    setEditedSection: PropTypes.func.isRequired,
    pendingCredits: PropTypes.array,
    addPendingCredit: PropTypes.func,
    removePendingCredit: PropTypes.func,
  }).isRequired,
  prefix: PropTypes.string,
  customHeader: PropTypes.element,
  isFeeProviderColumnVisible: PropTypes.bool,
  isRecordingType: PropTypes.bool,
};

export default FeeTable;
